import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  CircularProgress,
  Grid,
  Stack,
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
  Box,
} from '@mui/material';
import Modal from 'components/UI/DialogModal';
import { useTranslation } from 'react-i18next';
import MuiButton from 'components/UI/MuiButton/MuiButton';
import { Send } from '@mui/icons-material';

interface IEmployeeWithVoucher {
  employeeId: string;
  employeeName: string;
  employeeNameAr: string;
  employeeIdString: string;
  departmentName: string;
  departmentNameAr: string;
  jobName: string;
  jobNameAr: string;
  voucherCode?: string;
  expiryDate?: Date;
  isUsed: boolean;
  discount?: number;
  Discount?: number; // Handle both camelCase and PascalCase
  hasVoucher: boolean;
}

interface SelectedEmployee {
  employeeId: string;
  discount: number;
}

interface Props {
  open: boolean;
  loading: boolean;
  employees: IEmployeeWithVoucher[];
  hasModifyPermission: boolean;
  onClose: () => void;
  onSendVouchers: (selectedEmployees: SelectedEmployee[]) => void;
}

const VoucherEmployeesModal: React.FC<Props> = ({
  open,
  loading,
  employees,
  hasModifyPermission,
  onClose,
  onSendVouchers,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedEmployees, setSelectedEmployees] = useState<Map<string, number>>(new Map());

  const employeesWithoutVouchers = employees.filter(emp => !emp.hasVoucher);
  const hasEmployeesWithoutVouchers = employeesWithoutVouchers.length > 0;
  const hasSelectedEmployees = selectedEmployees.size > 0;

  // Reset selections when modal closes or employees change
  useEffect(() => {
    if (!open) {
      setSelectedEmployees(new Map());
    }
  }, [open]);

  const handleCheckboxChange = (employeeId: string, checked: boolean) => {
    setSelectedEmployees(prev => {
      const newMap = new Map(prev);
      if (checked) {
        newMap.set(employeeId, 0); // Default discount is 0
      } else {
        newMap.delete(employeeId);
      }
      return newMap;
    });
  };

  const handleDiscountChange = (employeeId: string, discount: number) => {
    setSelectedEmployees(prev => {
      const newMap = new Map(prev);
      newMap.set(employeeId, discount);
      return newMap;
    });
  };

  const handleSendVouchers = () => {
    const selected: SelectedEmployee[] = Array.from(selectedEmployees.entries()).map(([employeeId, discount]) => ({
      employeeId,
      discount
    }));
    onSendVouchers(selected);
  };

  // Debug: Log employees data to check discount values
  // useEffect(() => {
  //   if (open && employees.length > 0) {
  //     console.log('Employees with vouchers data:', employees.map(emp => ({
  //       employeeId: emp.employeeId,
  //       employeeName: emp.employeeName,
  //       hasVoucher: emp.hasVoucher,
  //       discount: emp.discount,
  //       Discount: (emp as any).Discount,
  //       raw: emp
  //     })));
  //   }
  // }, [open, employees]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('T_vouchers') || 'Vouchers'}
      maxWidth="xl"
      children={
        <Stack spacing={2} p={2}>
          {hasEmployeesWithoutVouchers && hasModifyPermission && (
            <Grid container justifyContent="flex-end" mb={2}>
              <MuiButton
                variant="contained"
                startIcon={<Send />}
                onClick={handleSendVouchers}
                disabled={loading || !hasSelectedEmployees}
                sx={{ bgcolor: 'primary.main', color: 'white' }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                    {t('T_sending') || 'Sending...'}
                  </>
                ) : (
                  t('T_send_vouchers') || 'Send Vouchers'
                )}
              </MuiButton>
            </Grid>
          )}
          
          {loading && employees.length === 0 ? (
            <Stack alignItems="center" p={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    {hasModifyPermission && hasEmployeesWithoutVouchers && (
                      <TableCell padding="checkbox">{t('T_select') || 'Select'}</TableCell>
                    )}
                    <TableCell>{t('T_employee_name') || 'Employee Name'}</TableCell>
                    <TableCell>{t('T_employee_id') || 'Employee ID'}</TableCell>
                    <TableCell>{t('T_department') || 'Department'}</TableCell>
                    <TableCell>{t('T_job') || 'Job'}</TableCell>
                    {hasModifyPermission && hasEmployeesWithoutVouchers && (
                      <TableCell>{t('T_discount') || 'Discount'}</TableCell>
                    )}
                    <TableCell>{t('T_voucher_code') || 'Voucher Code'}</TableCell>
                    <TableCell>{t('T_voucher_discount') || 'Voucher Discount'}</TableCell>
                    <TableCell>{t('T_expiry_date') || 'Expiry Date'}</TableCell>
                    <TableCell>{t('T_is_used') || 'Is Used'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => {
                    const isSelected = selectedEmployees.has(employee.employeeId);
                    const discount = selectedEmployees.get(employee.employeeId) ?? 0;
                    const showCheckbox = hasModifyPermission && !employee.hasVoucher;
                    
                    // Get discount value for display - directly access from employee object
                    const emp = employee as any;
                    let displayDiscount: number | null = null;
                    
                    if (employee.hasVoucher) {
                      // Try multiple ways to access the discount
                      if (emp.discount !== undefined && emp.discount !== null) {
                        displayDiscount = Number(emp.discount);
                      } else if (emp.Discount !== undefined && emp.Discount !== null) {
                        displayDiscount = Number(emp.Discount);
                      } else {
                        displayDiscount = 0;
                      }
                      
                      // Debug log for first employee with voucher
                      // if (employees.indexOf(employee) === 0) {
                      //   console.log('First employee discount debug:', {
                      //     employeeId: employee.employeeId,
                      //     hasVoucher: employee.hasVoucher,
                      //     empDiscount: emp.discount,
                      //     empDiscountType: typeof emp.discount,
                      //     empDiscountCapital: emp.Discount,
                      //     finalDisplayDiscount: displayDiscount,
                      //     fullEmployee: emp
                      //   });
                      // }
                    }

                    return (
                      <TableRow key={employee.employeeId}>
                        {showCheckbox && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleCheckboxChange(employee.employeeId, e.target.checked)}
                            />
                          </TableCell>
                        )}
                        {!showCheckbox && hasModifyPermission && hasEmployeesWithoutVouchers && (
                          <TableCell padding="checkbox"></TableCell>
                        )}
                        <TableCell>
                          {i18n.language === 'ar' ? employee.employeeNameAr : employee.employeeName}
                        </TableCell>
                        <TableCell>{employee.employeeIdString}</TableCell>
                        <TableCell>
                          {i18n.language === 'ar' ? employee.departmentNameAr : employee.departmentName}
                        </TableCell>
                        <TableCell>
                          {i18n.language === 'ar' ? employee.jobNameAr : employee.jobName}
                        </TableCell>
                        {showCheckbox && (
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={discount}
                              onChange={(e) => handleDiscountChange(employee.employeeId, parseFloat(e.target.value) || 0)}
                              disabled={!isSelected}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                        )}
                        {!showCheckbox && hasModifyPermission && hasEmployeesWithoutVouchers && (
                          <TableCell></TableCell>
                        )}
                        <TableCell>
                          {employee.voucherCode || '-'}
                        </TableCell>
                        <TableCell>
                          {employee.hasVoucher 
                            ? (employee as any).discount?.toFixed(2) ?? '0.00'
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {employee.expiryDate ? new Date(employee.expiryDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          {employee.hasVoucher ? (employee.isUsed ? t('T_yes') : t('T_no')) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {employees.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" textAlign="center" p={4}>
              {t('T_no_employees') || 'No employees found'}
            </Typography>
          )}
        </Stack>
      }
      actions={
        <Grid container alignItems={'center'} justifyContent={'space-between'} px={1}>
          <MuiButton
            variant="contained"
            sx={{ bgcolor: 'warning.main', color: 'white' }}
            size="large"
            onClick={onClose}
          >
            {t('T_close')}
          </MuiButton>
        </Grid>
      }
    />
  );
};

export default VoucherEmployeesModal;
